import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getUserSettings, updateUserSettings} from 'src/openapi/admin/apis/UserSettingsApi';
import {ResetPeriod as ResetPeriods} from 'src/openapi/admin/models/ResetPeriod';
import {useAppDispatch} from 'src/store/hooks';

import {
    NEXT_RESET_DATE_CONTROL,
    NEXT_RESET_DATE_REQUIRED_ERROR,
    NEXT_RESET_DATE_REQUIRED_MESSAGE,
} from './constants';

import type {MicroSiteUserSettings} from 'src/openapi/admin/models/MicroSiteUserSettings';

type Props = {
    accountId: number,
};

export const useUserResetSettings = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError, validationErrors} = useContext(ValidationContext);

    const getDispatchAction = useCallback((signal: AbortSignal) => apiDispatch(
        getUserSettings({accountId}, {signal}),
    ), [accountId, apiDispatch]);

    const saveDispatchAction = useCallback((
        microSiteUserSettings: MicroSiteUserSettings,
        signal: AbortSignal,
    ) => apiDispatch(
        updateUserSettings({accountId, microSiteUserSettings}, {signal}),
    ), [accountId, apiDispatch]);

    const editValidation = useCallback((data: MicroSiteUserSettings) => {
        if (data.nextResetDate) {
            removeError(NEXT_RESET_DATE_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: MicroSiteUserSettings) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (!data.nextResetDate) {
            addError({
                controlId: NEXT_RESET_DATE_CONTROL,
                code: NEXT_RESET_DATE_REQUIRED_ERROR,
                message: NEXT_RESET_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [validationErrors, addError]);

    return useCommonDataHook({
        initValue: {
            nextResetDate: null,
            resetPeriod: ResetPeriods._3,
            enableCreation: false,
            exceptUserReset: false,
        },
        getDispatchAction,
        saveDispatchAction,
        saveValidation,
        editValidation,
    });
};
